import { AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { map, Observable, startWith, tap } from 'rxjs';
import { Organization } from 'src/app/models/organization';
import { ViewCloningData } from './models/view-cloning-data';

@Component({
    selector: 'app-view-cloning-dialog',
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        MatAutocompleteModule,
        MatOptionModule,
        AsyncPipe,
    ],
    templateUrl: './view-cloning-dialog.component.html',
    styleUrl: './view-cloning-dialog.component.scss'
})
export class ViewCloningDialogComponent {
    /**
     * Champs du formulaire pour saisir le nom de la vue.
     */
    public viewNameFormControl: UntypedFormControl;

    /**
     * Champs du formulaire pour saisir l'organisation.
     */
    public organizationFormControl: UntypedFormControl;

    /**
     * Organisations affichées dans la liste déroulante.
     */
    public filteredOrganizations: Observable<Organization[]>;

    /**
     * Constructeur.
     * @param dialogRef
     * @param data
     */
    constructor(
        public dialogRef: MatDialogRef<ViewCloningDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ViewCloningData
    ) {}

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        // Initialise le Contrôle utilisé pour la saisie de l'organisation avec l'organisation par défaut.
        this.organizationFormControl = new UntypedFormControl(this.data.defaultOrganization, [Validators.required]);
        this.viewNameFormControl = new UntypedFormControl(null, [Validators.required]);
        this.data.organizationId = this.data.defaultOrganization.id;
        this.data.viewName = `${this.data.viewName} (copie)`;

        // S'abonne aux événements de saisie de l'utilisateur.
        this.filteredOrganizations = this.organizationFormControl.valueChanges.pipe(
            startWith(''),
            tap((value: string | Organization) => console.log('Change value : ', value)),
            map((value: string | Organization) => {
                if (typeof value === 'string') {
                    return this.filter((value as string) || '');
                } else {
                    if (value != null) {
                        this.data.organizationId = value.id;
                    }

                    return this.data.organizations;
                }
            })
        );
    }

    /**
     * Appellé par l'autocomplete pour déterminer le libellé à afficher pour une organization.
     * @param organization : L'organisation à afficher
     * @returns : Le libellé de l'organisation.
     */
    displayLabel(organization: Organization) {
        return organization ? organization.name : '';
    }

    /**
     * Appelée lorsque la liste déroulante se ferme.
     */
    public closed(): void {
        // Si le texte saisie ne correspond pas à une organisation mais à du texte saisie par l'utilisateur.
        if (typeof this.organizationFormControl.value === 'string') {
            // Réinitialise le champ avec l'organisation par défaut.
            this.organizationFormControl.setValue(this.data.defaultOrganization);
        }
    }

    /**
     * Appelée lorsque la liste déroulante s'ouvre.
     */
    public opened(): void {
        // Réinitialise le champ à vide.
        this.organizationFormControl.setValue('');
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    public save(): void {
        this.data.viewName = this.viewNameFormControl.value;
        this.dialogRef.close(this.data);
    }

    /**
     * Filtre les organisations correspondant au texte saisie par l'utilisateur.
     * @param value : texte saisie par l'utilisateur.
     * @returns Les organisations filtrées.
     */
    private filter(value: string): Organization[] {
        const filterValue = value.toLowerCase();
        return this.data.organizations.filter((organization) => organization.name.toLowerCase().includes(filterValue));
    }
}
